// Phase 10 (item 1 - "no approved item still a placeholder"): this was left
// as a ComingSoon stub pending "ledger-wise member balances", which Phase 6
// actually implemented (see MemberReceivablesPage.jsx's use of
// /engine/ledgers/{id}/party-balances). A member "advance" is simply a
// member whose subledger balance under the Member Receivables control
// ledger (or a dedicated Member Advance ledger, if configured in Billing
// Ledger Mapping) sits on the CREDIT side - i.e. they have paid more than
// they currently owe. This reuses that exact same endpoint/data, never a
// separately maintained "advance" counter.
import { useEffect, useMemo, useState } from "react";
import { api, formatErr } from "../../../../lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../../components/ui/table";
import { toast } from "sonner";
import { SectionCard, EmptyState } from "./shared";

export default function MemberAdvancesPage() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/society/accounting/reports/receivables");
        setReport(res.data || null);
      } catch (err) {
        toast.error(formatErr(err, "Could not load member advances"));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // A dedicated Advance ledger's own balance IS the advance (any nonzero
  // party balance there is an advance). Falling back to the shared
  // Receivables ledger, only a CREDIT-side party balance represents an
  // advance (a debit-side balance there is an ordinary amount still owed).
  const advances = useMemo(() => {
    return (report?.rows || []).map((row) => ({
      partyName: row.partyLabel,
      balance: Number(row.closingOutstanding || 0) - Number(row.advance || 0),
    })).filter((p) => p.balance < -0.01);
  }, [report]);

  const total = advances.reduce((sum, p) => sum + Math.abs(p.balance), 0);

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-extrabold text-[#111827]">Member Advances</h2>
        <p className="mt-1 text-sm text-[#6B7280]">Only a member's final net credit after opening dues, bills and receipts is shown here. A receipt against an existing due is not an advance.</p>
      </div>

      {loading ? (
        <div className="text-sm text-[#6B7280]">Loading...</div>
      ) : report?.configured === false ? (
        <div className="rounded-sm border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Member Receivables (or a dedicated Member Advance) ledger is not yet configured under Administration → Billing Ledger Mapping. Once set, advances derived from posted vouchers will appear here.
        </div>
      ) : (
        <SectionCard title="Members with a credit balance" subtitle={`Total advances: ₹ ${total.toLocaleString()}`}>
          <div className="overflow-x-auto rounded-sm border border-[#E5E7EB]">
            <Table>
              <TableHeader><TableRow><TableHead>Member</TableHead><TableHead className="text-right">Advance Balance</TableHead></TableRow></TableHeader>
              <TableBody>
                {advances.map((p) => (
                  <TableRow key={p.partyName}>
                    <TableCell className="font-semibold">{p.partyName}</TableCell>
                    <TableCell className="text-right">₹ {Math.abs(p.balance).toLocaleString()}</TableCell>
                  </TableRow>
                ))}
                {advances.length === 0 && <TableRow><TableCell colSpan={2}><EmptyState>No members currently have an advance balance.</EmptyState></TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </SectionCard>
      )}
    </div>
  );
}
